(ns ui.view.transaction-process.modals.attestation.view
  (:require
   [common.localization :refer [tr]]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.collapse.view :as collapse]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.state.session.sub :as session-sub]
   [ui.view.transaction-process.cards.property-list :as property-list]
   [ui.view.transaction-process.components.file-upload :as file-upload]
   [ui.view.transaction-process.modals.attestation.event :as event]
   [ui.view.transaction-process.modals.attestation.sub :as sub]
   [ui.view.transaction-process.modals.common :as common]
   [ui.view.transaction-process.sub :as tp-sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- flabel
  "Field label from the template field-config (→ schema fallback) — never a
   hardcoded `(tr ...)` for an entity value field."
  [field-configs field-kw]
  (tp-util/field-label field-kw (get field-configs field-kw)))

(defn- condition-editor
  [{:keys [ctx conditions]}]
  [:div
   [:label {:class "block text-sm m-0 font-medium text-gray-700 mb-1"} (tr :conditions)]
   [:div {:class "flex flex-col gap-2"}
    (doall
     (for [[idx condition] (map-indexed vector conditions)]
       ^{:key idx}
       [:div {:class "border border-gray-200 rounded-lg p-3 flex flex-col gap-2"}
        [:div {:class "flex items-center justify-between"}
         [:span {:class "text-sm m-0 font-medium text-gray-700"} (tr :transaction-process/condition-n [(inc idx)])]
         [:button {:class    "text-xs m-0 text-red-600 hover:underline"
                   :on-click (fn []
                               (rf/dispatch [::event/set-conditions ctx
                                             (vec (concat (subvec conditions 0 idx)
                                                          (subvec conditions (inc idx))))]))}
          (tr :remove)]]
        [labelled-input/view
         {:label      (tr :title)
          :mandatory? true
          :data-type  :data.type/text-simple
          :value      (:title condition)
          :on-change  (fn [v] (rf/dispatch [::event/set-conditions ctx (assoc-in conditions [idx :title] v)]))}]
        [labelled-input/view
         {:label      (tr :status)
          :mandatory? true
          :data-type  :data.type/keyword
          :value      (:status condition)
          :options    @(rf/subscribe [::sub/condition-status-options])
          :on-change  (fn [v] (rf/dispatch [::event/set-conditions ctx (assoc-in conditions [idx :status] v)]))}]
        [labelled-input/view
         {:label     (tr :description)
          :data-type :data.type/text-multiline
          :value     (:description condition)
          :on-change (fn [v] (rf/dispatch [::event/set-conditions ctx (assoc-in conditions [idx :description] v)]))}]]))]
   [:div {:class (when (seq conditions) "mt-3")}
    [button/sm-secondary-hollow
     {:on-click (fn [] (rf/dispatch [::event/set-conditions ctx (conj conditions {:title "" :description ""})]))}
     [:i {:class "fal fa-plus text-xs m-0"}]
     (tr :transaction-process/add-condition)]]])

(defn- existing-attachments
  [entity-id]
  (when entity-id
    (let [entity (rf/subscribe [::session-sub/entity entity-id])]
      (->> (:common/attachments @entity)
           (map (fn [ref] @(rf/subscribe [::session-sub/entity (or (:db/id ref) ref)])))
           (remove nil?)
           (remove :common/disabled?)
           (sort-by :attachment/uploaded-on)))))

(defn- pending-files-section
  [{:keys [ctx pending-files entity-id]}]
  [:div
   [:label {:class "block text-sm m-0 font-medium text-gray-700 mb-1"} (tr :documents)]
   [file-upload/attachments-list
    {:attachments (existing-attachments entity-id)
     :target-id   entity-id}]
   [file-upload/staged-files
    {:files     pending-files
     :on-remove (fn [idx] (rf/dispatch [::event/remove-pending-file ctx idx]))}]
   [file-upload/drop-zone
    {:multiple? true
     :label     (tr :drop-files-browse)
     :on-files  (fn [files]
                  (doseq [file files]
                    (rf/dispatch [::event/add-pending-file ctx file])))}]])

(defn- detail-fields-section
  "The slot's read-only detail fields, disclosed in place.

   These used to open a second modal stacked on the verification modal, to show
   a handful of read-only answers belonging to the very verification already on
   screen. It also rendered full-bleed rather than at its declared 720px, since
   `modal/view`'s :size is not applied to a nested modal. Inline behind a
   disclosure instead: the form underneath stays visible, and the answers sit
   next to the outcome they justify. Collapsed by default so they don't push
   Outcome and Comment down the form."
  [{:keys [_detail-fields _field-configs _process]}]
  (let [open? (r/atom false)]
    (fn [{:keys [detail-fields field-configs process]}]
      [:div {:class "flex flex-col gap-2"}
       [:button
        {:type     "button"
         :class    "w-fit text-sm m-0 text-primary-regular hover:underline"
         :on-click (fn [] (swap! open? not))}
        (tr (if @open? :show-less-details :show-details))]
       [collapse/view
        {:open? @open?}
        [:div {:class "grid grid-cols-2 gap-x-6 gap-y-4 pt-1"}
         (doall
          (for [field-kw detail-fields
                :let [field-config (get field-configs field-kw)]
                :when (tp-util/field-visible?
                       {:field-config field-config
                        :value-lookup process})]
            ^{:key field-kw}
            [property-list/read-only-field
             {:field-kw     field-kw
              :field-config field-config
              :entity-data  process}]))]]])))

(defn view
  [{:keys [ctx]}]
  (let [edit-mode?           @(rf/subscribe [::sub/edit-mode? ctx])
        submitting?          @(rf/subscribe [::sub/submitting? ctx])
        error                @(rf/subscribe [::sub/error ctx])
        type-label           @(rf/subscribe [::sub/type-label ctx])
        outcome              @(rf/subscribe [::sub/outcome ctx])
        date                 @(rf/subscribe [::sub/date ctx])
        comment              @(rf/subscribe [::sub/comment ctx])
        conditions           @(rf/subscribe [::sub/conditions ctx])
        pending-files        @(rf/subscribe [::sub/pending-files ctx])
        entity-id            @(rf/subscribe [::sub/entity-id ctx])
        outcome-options      @(rf/subscribe [::sub/outcome-options ctx])
        field-configs        @(rf/subscribe [::sub/field-configs ctx])
        context-fields       @(rf/subscribe [::sub/context-fields ctx])
        detail-fields        @(rf/subscribe [::sub/detail-fields ctx])
        process              @(rf/subscribe [::tp-sub/process ctx])
        date-visible?        @(rf/subscribe [::sub/date-visible? ctx])
        mandatory-fields     @(rf/subscribe [::sub/mandatory-fields ctx])
        conditions-visible?  @(rf/subscribe [::sub/conditions-visible? ctx])
        ready-errors         @(rf/subscribe [::sub/ready-errors ctx])
        save-draft-disabled? @(rf/subscribe [::sub/save-draft-disabled? ctx])]
    [modal/view
     {:visible? true
      :hide!    #(rf/dispatch [::common/close ctx])}
     (if edit-mode? (tr :transaction-process/edit-verification) (tr :transaction-process/log-verification))
     [:div {:class "flex flex-col gap-4"}
      [common/type-label-header type-label]
      [common/error-banner error]
      (when (seq context-fields)
        [:div {:class "grid grid-cols-2 gap-x-6 gap-y-4"}
         (doall
          (for [field-kw context-fields]
            ^{:key field-kw}
            [property-list/read-only-field
             {:field-kw field-kw
              :field-config (get field-configs field-kw)
              :entity-data process}]))])
      (when date-visible?
        [labelled-input/view
         {:label           (flabel field-configs :attestation/date)
          :mandatory?      (contains? mandatory-fields :attestation/date)
          :container-class "w-56 max-w-full"
          :data-type       :data.type/date
          :value           date
          :on-change       (fn [v] (rf/dispatch [::event/set-field ctx :attestation/date v]))}])
      (when (seq detail-fields)
        [detail-fields-section
         {:detail-fields detail-fields
          :field-configs field-configs
          :process       process}])
      [labelled-input/view
       {:label      (flabel field-configs :attestation/outcome)
        :mandatory? (contains? mandatory-fields :attestation/outcome)
        :data-type  :data.type/keyword
        :value      outcome
        :options    outcome-options
        :on-change  (fn [v] (rf/dispatch [::event/set-field ctx :attestation/outcome v]))}]
      [labelled-input/view
       {:label      (flabel field-configs :attestation/comment)
        :mandatory? (contains? mandatory-fields :attestation/comment)
        :data-type  :data.type/text-multiline
        :value      comment
        :on-change  (fn [v] (rf/dispatch [::event/set-field ctx :attestation/comment v]))}]
      (when conditions-visible?
        [condition-editor {:ctx ctx :conditions conditions}])
      [pending-files-section {:ctx ctx :pending-files pending-files :entity-id entity-id}]]
     [common/save-footer
      {:submitting?          submitting?
       :save-draft-disabled? save-draft-disabled?
       :ready-errors         ready-errors
       :on-cancel            #(rf/dispatch [::common/close ctx])
       :on-save-draft        #(rf/dispatch [::event/save ctx true])
       :on-save-ready        #(rf/dispatch [::event/save ctx false])}]]))
